import { useEffect, type ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import trTR from 'antd/locale/tr_TR';
import { antdTheme, demoTheme, getThemeCSSVariables, fontFamilySans } from '../theme';

interface ThemeProviderProps {
  children: ReactNode;
}

const GLOBAL_STYLE_ID = 'raporgor-theme-globals';

export function ThemeProvider({ children }: ThemeProviderProps) {
  useEffect(() => {
    let el = document.getElementById(GLOBAL_STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = GLOBAL_STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = `
      :root { ${getThemeCSSVariables(demoTheme)} }

      html, body, #root {
        margin: 0;
        padding: 0;
        height: 100%;
        font-family: ${fontFamilySans};
        font-size: 13px;
        line-height: 1.45;
        font-feature-settings: 'tnum', 'ss01';
        font-variant-numeric: tabular-nums;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        color: var(--color-text-primary);
        background: var(--color-page-bg);
      }

      /* Kurumsal finans aesthetic — nümerik kolonlar için tablobased numerals */
      .tabular, .num, .ag-cell-value { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum'; }

      /* AG Grid finance-grade density override — 32px row, 36px header, 13px */
      .ag-theme-alpine {
        --ag-font-family: ${fontFamilySans};
        --ag-font-size: 13px;
        --ag-grid-size: 4px;
        --ag-row-height: 32px;
        --ag-header-height: 36px;
        --ag-header-background-color: var(--color-hover-bg);
        --ag-header-foreground-color: var(--color-text-secondary);
        --ag-border-color: var(--color-border);
        --ag-row-border-color: var(--color-border-light);
        --ag-odd-row-background-color: #fbfcfd;
        --ag-row-hover-color: var(--color-hover-bg);
        --ag-selected-row-background-color: var(--color-accent-bg);
        --ag-range-selection-border-color: var(--color-primary);
        --ag-checkbox-checked-color: var(--color-primary);
        --ag-header-cell-moving-background-color: var(--color-selected-bg);
        --ag-control-panel-background-color: var(--color-card-bg);
        --ag-subheader-background-color: var(--color-hover-bg);
        --ag-input-focus-border-color: var(--color-primary);
        --ag-input-focus-box-shadow: none;
      }

      .ag-theme-alpine .ag-header-cell-text {
        font-weight: 600;
        font-size: 11px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
      }
      .ag-theme-alpine .ag-header-cell {
        padding-left: 12px;
        padding-right: 12px;
      }
      .ag-theme-alpine .ag-cell {
        display: flex;
        align-items: center;
        padding-left: 12px;
        padding-right: 12px;
        border-right: none;
      }
      /* Numeric kolonlar (cellClass="tabular ag-right") */
      .ag-theme-alpine .ag-cell.ag-right {
        justify-content: flex-end;
        font-variant-numeric: tabular-nums;
        font-feature-settings: 'tnum';
      }
      .ag-theme-alpine .ag-header-cell.ag-right-header .ag-header-cell-label {
        justify-content: flex-end;
      }

      /* Sort icons — soluk slate, primary değil parlak mavi */
      .ag-theme-alpine .ag-sort-indicator-icon,
      .ag-theme-alpine .ag-icon-asc,
      .ag-theme-alpine .ag-icon-desc {
        color: var(--color-text-muted);
        opacity: 0.8;
      }

      /* Pinned kolon ayrışık hairline */
      .ag-theme-alpine .ag-pinned-left-cols-container,
      .ag-theme-alpine .ag-pinned-left-header {
        border-right: 1px solid var(--color-border) !important;
      }

      /* Row selected — sol 2px primary aksent + subtle bg */
      .ag-theme-alpine .ag-row.ag-row-selected {
        background-color: var(--color-accent-bg);
        box-shadow: inset 2px 0 0 var(--color-primary);
      }

      /* Pagination bar — flat, sıkı, card bg */
      .ag-theme-alpine .ag-paging-panel {
        border-top: 1px solid var(--color-border);
        background: var(--color-card-bg);
        color: var(--color-text-secondary);
        font-size: 12px;
        padding: 4px 14px;
        min-height: 36px;
      }
      .ag-theme-alpine .ag-paging-button {
        border-radius: 3px;
        margin: 0 2px;
        min-width: 24px;
        height: 24px;
        transition: background .1s ease;
      }
      .ag-theme-alpine .ag-paging-button:hover:not(.ag-disabled) {
        background: var(--color-hover-bg);
        color: var(--color-text-primary);
      }
      .ag-theme-alpine .ag-paging-button.ag-disabled {
        opacity: 0.4;
      }
      .ag-theme-alpine .ag-paging-page-summary-panel {
        font-variant-numeric: tabular-nums;
      }
      .ag-theme-alpine .ag-picker-field-wrapper {
        border-radius: 3px;
      }

      /* Cell focus — subtle primary outline */
      .ag-theme-alpine .ag-cell-focus {
        border: 1px solid var(--color-primary) !important;
        outline: none !important;
      }

      /* Filter popup */
      .ag-theme-alpine .ag-popup-child {
        border: 1px solid var(--color-border);
        border-radius: 4px;
        box-shadow: 0 6px 20px var(--shadow-color-strong);
      }

      /* Scrollbar polish */
      ::-webkit-scrollbar { width: 10px; height: 10px; }
      ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 0; border: 2px solid transparent; background-clip: padding-box; }
      ::-webkit-scrollbar-track { background: transparent; }
    `;
  }, []);

  return (
    <ConfigProvider locale={trTR} theme={antdTheme}>
      {children}
    </ConfigProvider>
  );
}
